import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { getContentById } from '../data/content';
import { getCategoryPhotoObject } from '../lib/unsplash';
import UnsplashImage from '../components/UnsplashImage';
import Icon from '../components/shared/Icon';
import Comments from '../components/Comments';
import ClapButton from '../components/shared/ClapButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookOpen } from '@fortawesome/free-solid-svg-icons';
import { PageHeader } from '../design-system';

export default function StoryDetail() {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFirestoreContent, setIsFirestoreContent] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const [photoObject, setPhotoObject] = useState(null);

  // Try to load from Firestore first (approved user submissions)
  useEffect(() => {
    if (!storyId) {
      setLoading(false);
      return;
    }

    const contentRef = doc(db, 'content', storyId);
    const unsubscribe = onSnapshot(
      contentRef,
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          if (data.type === 'story') {
            // Convert Firestore content to story format
            setStory({
              id: doc.id,
              title: data.title,
              description: data.description,
              category: data.content.category || 'Story',
              ageRange: data.content.ageRange || 'All ages',
              image: data.content.imageUrl ? null : '📖',
              imageUrl: data.content.imageUrl,
              imageSearchQuery: data.imageSearchQuery,
              content: data.content.content || data.content.storyText || '',
              readAloud: data.content.readAloud || false,
            });
            // Use stored unsplashPhoto if available
            if (data.unsplashPhoto) {
              setPhotoObject(data.unsplashPhoto);
            }
            // Store uploaded image info
            setStory(prev => ({
              ...prev,
              unsplashPhoto: data.unsplashPhoto,
              uploadedImageUrl: data.uploadedImageUrl,
              imageCaption: data.imageCaption
            }));
            setIsFirestoreContent(true);
            setLoading(false);
            return;
          }
        }
        
        // Not found in Firestore, try static content
        const staticStory = getContentById('stories', storyId);
        if (staticStory) {
          setStory(staticStory);
          setIsFirestoreContent(false);
        }
        setLoading(false);
      },
      (error) => {
        // Error loading from Firestore, try static content
        const staticStory = getContentById('stories', storyId);
        if (staticStory) {
          setStory(staticStory);
          setIsFirestoreContent(false);
        }
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [storyId]);

  // Load photo object if not already set
  useEffect(() => {
    if (story && !photoObject) {
      getCategoryPhotoObject('stories', story, 0).then(photo => {
        if (photo) {
          setPhotoObject(photo);
        }
      });
    }
  }, [story, photoObject]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg text-text-secondary">Loading story...</div>
        </div>
      </div>
    );
  }

  if (!story) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-white flex items-center justify-center">
        <div className="text-center">
          <div className="mb-4"><Icon name="🔍" size="4x" className="text-text-secondary" /></div>
          <div className="text-lg text-text-secondary">Story not found</div>
          <button
            onClick={() => navigate('/explore')}
            className="mt-4 px-6 py-3 rounded-xl font-semibold bg-gradient-to-r from-primary to-primary-light text-white hover:-translate-y-0.5 hover:shadow-lg transition-all"
          >
            Back to Explore
          </button>
        </div>
      </div>
    );
  }

  const handleReadAloud = () => {
    if ('speechSynthesis' in window) {
      if (isReading) {
        window.speechSynthesis.cancel();
        setIsReading(false);
      } else {
        const utterance = new SpeechSynthesisUtterance(story.content);
        utterance.rate = 0.9;
        utterance.pitch = 1;
        utterance.onend = () => setIsReading(false);
        window.speechSynthesis.speak(utterance);
        setIsReading(true);
      }
    } else {
      alert('Read aloud is not supported in your browser');
    }
  };

  return (
    <div className="min-h-screen bg-white pt-14 pb-20 md:pt-0 md:pb-0">
      <PageHeader
        title={story.title}
        onBack={() => navigate('/explore')}
        maxWidth="896px"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-10 py-6 sm:py-8 md:py-10">

        <div className="bg-white rounded-card shadow-card overflow-hidden">
          {/* Hero Section */}
          <div className="w-full h-[200px] sm:h-[300px] bg-surface flex items-center justify-center rounded-t-card overflow-hidden relative">
            {story.unsplashPhoto ? (
              <UnsplashImage
                photo={story.unsplashPhoto}
                alt={story.title}
                className="w-full h-full object-cover"
                triggerDownloadOnClick={false}
              />
            ) : story.uploadedImageUrl ? (
              <div className="relative w-full h-full">
                <img 
                  src={story.uploadedImageUrl} 
                  alt={story.title}
                  className="w-full h-full object-cover"
                />
                {story.imageCaption && (
                  <div className="absolute bottom-2 right-2 bg-black/40 backdrop-blur-sm px-2 py-1 rounded text-xs text-white">
                    {story.imageCaption}
                  </div>
                )}
              </div>
            ) : photoObject ? (
              <UnsplashImage
                photo={photoObject}
                alt={story.title}
                className="w-full h-full object-cover"
                triggerDownloadOnClick={false}
              />
            ) : story.imageUrl ? (
              <img 
                src={story.imageUrl} 
                alt={story.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <Icon name={story.image || '📖'} size="5x" className="text-primary" />
            )}
          </div>

          <div className="p-6 sm:p-8 md:p-10">
            {/* Title and Meta */}
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3">
                <div className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide">
                  {story.category}
                </div>
                <span className="text-text-secondary">•</span>
                <div className="text-[11px] font-semibold text-text-secondary">
                  Ages {story.ageRange}
                </div>
              </div>
              <h1 className="font-sans text-2xl sm:text-3xl md:text-4xl font-semibold text-text-primary mb-3 tracking-tight">
                {story.title}
              </h1>
              <p className="text-base sm:text-lg text-text-secondary leading-relaxed">
                {story.description}
              </p>
            </div>

            {/* Read Aloud Button and Clap */}
            <div className="mb-6 sm:mb-8 flex flex-wrap items-center gap-3">
              {story.readAloud && (
                <button
                  onClick={handleReadAloud}
                  className={`px-6 py-3 sm:py-4 rounded-xl font-semibold transition-all ${
                    isReading
                      ? 'bg-accent text-white'
                      : 'bg-primary text-white hover:bg-primary-dark shadow-button hover:shadow-button-hover'
                  }`}
                >
                  {isReading ? <><Icon name="⏸️" className="mr-1" /> Pause Reading</> : <><Icon name="🔊" className="mr-1" /> Read Aloud</>}
                </button>
              )}
              <ClapButton 
                contentId={storyId} 
                contentType="story" 
                size="lg"
              />
            </div>

            {/* Story Content */}
            <div className="mb-6 sm:mb-8">
              <div className="prose prose-lg max-w-none">
                <div className="text-base sm:text-lg text-text-primary leading-relaxed whitespace-pre-line">
                  {story.content}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Comments Section */}
        <Comments contentId={storyId} contentType="story" />
      </div>
    </div>
  );
}

